import React, {useState} from 'react';

// helpers
import {useLocation} from 'react-router-dom';
import {checkIsMenuItemActive} from './helpers';

// styles
import {
  IconWrapper,
  MenuContainer,
  MenuRowContent,
  MenuRowWrapper,
  MenuText,
  StyledAction,
  StyledLink,
  SubMenuContainer,
} from './Menu-styles';

// types
import {MenuItem} from '../../hooks';

type Props = {
  menuItems: MenuItem[];
  closeMenu?: () => void;
  level?: number;
};

const Menu = ({menuItems, closeMenu, level = 0}: Props) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleSubmenu = (itemText: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemText)
        ? prev.filter((item) => item !== itemText)
        : [...prev, itemText]
    );
  };

  const handleMenuRowClick = (item: MenuItem) => {
    if (item.children) {
      toggleSubmenu(item.text);
    } else if (item.action) {
      item.action();
    }
    if (closeMenu && !item.children) {
      closeMenu();
    }
  };

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    item: MenuItem,
    isActive: boolean,
  ) => {
    if (item.children) {
      e.preventDefault();
      toggleSubmenu(item.text);
    } else if (isActive) {
      e.preventDefault();
      window.location.href = item.rootPath!;
    } else {
      handleMenuRowClick(item);
    }
  };

  return (
    <MenuContainer level={level}>
      {menuItems.map((item) => {
        const isActive = checkIsMenuItemActive(
          location.pathname,
          item.rootPath,
        );
        const isExpanded = expandedItems.includes(item.text);

        const content = (
          <MenuRowContent active={isActive}>
            {item.icon && ( // Make icon wrapper conditional
              <IconWrapper active={isActive}>{item.icon}</IconWrapper>
            )}
            <MenuText active={isActive} bold="bold">
              {item.text}
            </MenuText>
          </MenuRowContent>
        );

        return (
          <React.Fragment key={item.text}>
            <MenuRowWrapper active={isActive}>
              {item.rootPath ? (
                <StyledLink
                  to={item.rootPath}
                  onClick={(e) => handleLinkClick(e, item, isActive)}>
                  {content}
                </StyledLink>
              ) : (
                <StyledAction onClick={() => handleMenuRowClick(item)}>
                  {content}
                </StyledAction>
              )}
            </MenuRowWrapper>
            {item.children && isExpanded && (
              <SubMenuContainer>
                <Menu
                  menuItems={item.children}
                  closeMenu={closeMenu}
                  level={level + 1}
                />
              </SubMenuContainer>
            )}
          </React.Fragment>
        );
      })}
    </MenuContainer>
  );
};

export default Menu;
